import { useEffect, useMemo, useState } from "react";
import Autoplay from "embla-carousel-autoplay";
import type { CarouselApi } from "@/components/ui/carousel";

/**
 * Autoplay for a strip that moves on its own: it advances every `delay` ms,
 * pauses while the pointer is over it, and carries on after a swipe. Pass
 * `plugins` to the Carousel's `plugins` and `setApi` to its `setApi`.
 *
 * Embla re-initialises its plugins on every resize, and an image loading in
 * the strip is a resize. Left to itself the plugin starts again on each of
 * those, even under the pointer, and even after it was stopped for reduced
 * motion. So the plugin never starts itself; this hook starts it, and again
 * after every re-init, unless the pointer is over the strip. A visitor who
 * asked for reduced motion gets no plugin at all, and until that preference
 * is known (the prerender, the first client render) the strip stands still.
 *
 * A strip also stands still while it is off screen. Every step of a moving
 * strip rewrites its transform each frame, and the home page has four of
 * them: about 90 DOM changes a second, scrolled past or not, which Mixpanel's
 * session recorder turned into an upload backlog that made the whole page
 * stutter (4 Nov). Only the strip being looked at moves now.
 */
export function useAutoplay(delay: number) {
  const [moves, setMoves] = useState(false);
  const [api, setApi] = useState<CarouselApi>();

  useEffect(() => {
    setMoves(!window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }, []);

  const plugin = useMemo(
    () => Autoplay({ delay, playOnInit: false, stopOnInteraction: false, stopOnMouseEnter: true }),
    [delay],
  );

  useEffect(() => {
    if (!api || !moves) return;
    const root = api.rootNode();
    let over = root.matches(":hover");
    // Assume on screen until the observer says otherwise, so a browser
    // without IntersectionObserver keeps the old behaviour.
    let inView = true;
    const enter = () => (over = true);
    const leave = () => (over = false);
    const resume = () => {
      if (!over && inView) plugin.play();
    };
    const io =
      typeof IntersectionObserver === "undefined"
        ? null
        : new IntersectionObserver(([entry]) => {
            inView = entry?.isIntersecting ?? true;
            if (inView) resume();
            else plugin.stop();
          });
    io?.observe(root);
    root.addEventListener("mouseenter", enter);
    root.addEventListener("mouseleave", leave);
    api.on("reInit", resume);
    resume();
    return () => {
      io?.disconnect();
      root.removeEventListener("mouseenter", enter);
      root.removeEventListener("mouseleave", leave);
      api.off("reInit", resume);
    };
  }, [api, moves, plugin]);

  const plugins = useMemo(() => (moves ? [plugin] : []), [moves, plugin]);
  return { plugins, setApi };
}
